import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom/client';
import { shouldRedirectOn401 } from './fetch-401-policy';

// When a new portal deploy invalidates old JS chunks, Vite fires this event.
// Auto-reload so the user gets the new version instead of a broken error screen.
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

// Rewrite all /api/ fetch calls to go directly to the API container, bypassing nginx.
const API_BASE = (
  (import.meta.env.VITE_API_BASE_URL as string) ||
  'https://ca-onramp-api.icyplant-45887311.westus2.azurecontainerapps.io'
).replace(/\/$/, '');

const _fetch = window.fetch.bind(window);
window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const url = typeof input === 'string' && input.startsWith('/api/')
    ? `${API_BASE}${input}`
    : input;
  let res: Response;
  try {
    res = await _fetch(url, init);
  } catch {
    return _fetch(input, init);
  }
  if (res.status === 401) {
    const headers = init?.headers as Record<string, string> | undefined;
    const hadAuth = Boolean(headers?.['Authorization'] || headers?.['authorization']);
    const urlStr = typeof url === 'string' ? url : url.toString();
    if (shouldRedirectOn401(urlStr, hadAuth)) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.replace('/login');
    }
  }
  return res;
};

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Unable to preload CSS',
  'Loading chunk',
  'Loading CSS chunk',
  'ChunkLoadError',
];

function isChunkError(err: Error | null): boolean {
  if (!err) return false;
  const msg = err.message + (err.name ?? '') + ((err as any).stack ?? '');
  return CHUNK_ERROR_PATTERNS.some(p => msg.includes(p));
}

class ErrorBoundary extends React.Component<
  { children: ReactNode },
  { error: Error | null; reloading: boolean }
> {
  state = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Chunk/module load errors are always stale-cache after a deploy — auto-reload.
    if (isChunkError(error) && !this.state.reloading) {
      this.setState({ reloading: true });
      // Small delay so we don't loop if reload itself fails
      setTimeout(() => window.location.reload(), 300);
    }
  }

  render() {
    if (this.state.reloading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', background: '#f9fafb', fontFamily: 'system-ui, sans-serif' }}>
          <p style={{ color: '#6b7280', fontSize: 14 }}>Updating to latest version…</p>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh', background: '#f9fafb',
          fontFamily: 'system-ui, sans-serif',
        }}>
          <div style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
            padding: 32, maxWidth: 420, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
          }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>⚠️</p>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Something went wrong
            </h2>
            <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>
              This page ran into an error. Try going back or refreshing.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => { this.setState({ error: null, reloading: false }); window.history.back(); }}
                style={{ padding: '8px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                  border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#374151' }}
              >
                Go back
              </button>
              <button
                onClick={() => window.location.reload()}
                style={{ padding: '8px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8,
                  border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer' }}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't retry 401s — the fetch interceptor above already handles them
      retry: (count, error) => {
        const msg = error instanceof Error ? error.message : String(error);
        if (msg.includes('401') || msg.includes('Unauthorized')) return false;
        return count < 2;
      },
      retryDelay: (attempt) => Math.min(500 * 2 ** attempt, 5000),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
